import { DateTime } from 'luxon';
import { PaginationRequest } from '../pagination/pagination';
import { Admin } from '../admin/admin';
import { CycleBillingTerms } from '../order/order';
import Dinero from 'dinero.js';
import { UpsertRepresentativeRequest } from '../representatives/representative';
import { StateProvince } from '../locales/locales';

export type User = {
  readonly id: number;
  readonly name: string;
  readonly email: string;
  readonly secondaryEmails?: string[];
  readonly company: string;
  readonly phone: string;
  readonly tagPricing?: TagPricing;
  readonly type: UserType;
  readonly addedOn: string;
  readonly status: UserStatus;
  readonly isDeleted: boolean;
  readonly chargeTax: boolean;
  readonly doNoRent: boolean;
  readonly paymentBan: boolean;
  readonly requirePoNum: boolean;
  readonly requireCompanyRep: boolean;
  readonly userReqDamageWaiver?: boolean;
  readonly firstRental?: string;
  readonly ordersPlaced?: number;
  readonly stripeCustomerId?: string;
  readonly cycleBillingTerms: CycleBillingTerms;
  readonly structureType: 'user' | 'company';
  readonly creditLimit?: Dinero.Dinero;
  readonly balance?: Dinero.Dinero;
  readonly processingFeeOverride?: ProcessingFeeOverride;
  readonly defaultSalesRepId?: number;
  readonly defaultTaxCode?: string;
};

export type TagPricing = {
  readonly id: number;
  readonly name: string;
  readonly discount: number;
};

export type UserAddress = {
  readonly id: number;
  readonly userId: number;
  readonly name: string;
  readonly phone?: string;
  readonly companyName?: string;
  readonly address1: string;
  readonly address2: string;
  readonly city: string;
  readonly state: string;
  readonly zip: string;
  readonly country: string;
  readonly type: 'delivery' | 'billing';
  readonly customDeliveryLocation?: string;
  readonly isGoogleGenerated: boolean;
  readonly isDefault: boolean;
  readonly isDeleted: boolean;
};

export type UserEquipment = {
  readonly id: string;
  readonly createdAt: DateTime;
  readonly updatedAt: DateTime;
  readonly userId: number;
  readonly description: string;
  readonly make: string | undefined;
  readonly model: string | undefined;
  readonly serialNumber: string | undefined;
};

export type UserEquipmentRequest = Omit<UserEquipment, 'id' | 'createdAt' | 'updatedAt'>;

export type SearchUserEquipmentRequest = {
  userId: number;
  keyword: string;
  pageSize?: number;
  pageNumber?: number;
};

export enum UserType {
  Guest = 'Guest',
  Registered = 'Registered',
  Cash = 'Cash',
}

export enum UserStatus {
  Inactive = 'Inactive',
  Active = 'Active',
}

export enum ProcessingFeeOverride {
  ApplyFee = 'ApplyFee',
  WaiveFee = 'WaiveFee',
}

export enum ProcessingFeeOverrideConf {
  ApplyFee = 'apply_fee',
  OptionallyApplyFee = 'optionally_apply_fee',
  OptionallyWaiveFee = 'optionally_waive_fee',
}

export class UserSearchRequest extends PaginationRequest {
  isDeleted?: boolean;
  keyword?: string;
  driversLicenseNumber?: string;
  driversLicenseStateId?: number;
  email?: string;
  statusFilter?: UserStatus;
  cashCustomerFirst?: boolean;
  excludeUserIds?: number[];
  sortBy?: 'name' | 'addedOn';
  sortDirection?: 'asc' | 'desc';
}

export type UpdateUserRequest = Omit<User, 'id' | 'addedOn' | 'type' | 'creditLimit'> & {
  newPassword?: string;
  creditLimit?: number;
};

export type UserInternalNote = {
  readonly id: number;
  readonly userId: number;
  readonly comment: string;
  readonly author?: Admin;
  readonly created: string;
  readonly edited: boolean;
};

export type UserInternalNoteRequest = Omit<UserInternalNote, 'id' | 'created'>;

export type UserAddressCreateRequest = Omit<UserAddress, 'id' | 'isDefault'>;

export class UserAddressSearchRequest extends PaginationRequest {
  userId?: number;
  keyword?: string;
  allowDeleted?: boolean;
}

export type UserDriverLic = {
  readonly id: number;
  readonly userId: number;
  readonly fullName: string;
  readonly driverLicNum: string;
  readonly expirationDate: DateTime;
  readonly state?: StateProvince;
  readonly dateOfBirth?: DateTime;
};

export type UserDriverLicRequest = Omit<UserDriverLic, 'id' | 'userId' | 'state'> & {
  updatedBy?: number;
  stateId?: number;
  skipCheck?: boolean;
};

export type UserDriverLicScanRequesst = {
  frontImage: string;
  frontImageMimeType: string;
  frontImageAutoCaptured: boolean;
  backImage: string;
  backImageMimeType: string;
  backImageAutoCaptured: boolean;
  rawTrackString: string;
};

export type UserDriverLicScanResponse = Omit<UserDriverLic, 'id'>;

export type UserDocument = {
  readonly id: number;
  readonly userId: number;
  readonly fileName: string;
  readonly fileType: string;
  readonly createdAt: string;
  readonly documentType: UserDocumentType;
  readonly status: UserDocumentStatus;
};

export enum UserDocumentType {
  Insurance = 'Insurance',
  License = 'License',
  OtherCheckout = 'OtherCheckout',
  Other = 'Other',
}

export enum DatabaseUserDocumentType {
  Insurance = 1,
  License = 2,
  OtherCheckout = 3,
  Other = 4,
}

export enum UserDocumentStatus {
  Pending = 'Pending',
  Approved = 'Approved',
  Rejected = 'Rejected',
  Deleted = 'Deleted',
}

export type CreateUserDocumentRequest = {
  readonly userId: number;
  readonly base64File: string;
  readonly documentType: UserDocumentType;
  readonly fileType: string;
};

export type UserUpdateDamageWaiverStatusRequest = {
  readonly userId: number;
  readonly hasDamageWaiverRequired: boolean;
};

export type UpdateInsuranceDocStatusRequest = {
  readonly userId: number;
  readonly documentId: number;
  readonly documentStatus: UserDocumentStatus;
};

export type CreateCustomerRequest = {
  readonly name: string;
  readonly withoutEmail: boolean;
  readonly type: 'User' | 'Company';
  readonly email?: string;
  readonly company?: string;
  readonly phone?: string;
};

export type CreateUserRequest = CreateCustomerRequest & { readonly type: 'User' };

export type CreateCompanyRequest = CreateCustomerRequest & {
  readonly type: 'Company';
  readonly representatives: UpsertRepresentativeRequest[];
};
